import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Search, User, Users, CheckSquare, Calendar, FileText,
  Receipt, Mail, Clock, ArrowRight, Command, Phone, MapPin,
  Tag, Briefcase, AlertCircle
} from "lucide-react";

const RESULT_TYPES = {
  client: { icon: Users, label: "Klient", color: "bg-blue-100 text-blue-700", path: "/klienci" },
  task: { icon: CheckSquare, label: "Zadanie", color: "bg-green-100 text-green-700", path: "/zadania" },
  event: { icon: Calendar, label: "Wydarzenie", color: "bg-purple-100 text-purple-700", path: "/kalendarz" },
  invoice: { icon: Receipt, label: "Faktura", color: "bg-orange-100 text-orange-700", path: "/faktury" },
  document: { icon: FileText, label: "Dokument", color: "bg-slate-100 text-slate-700", path: "/dokumenty" },
  email: { icon: Mail, label: "E-mail", color: "bg-pink-100 text-pink-700", path: "/e-maile" },
  user: { icon: User, label: "Uzytkownik", color: "bg-amber-100 text-amber-700", path: "/ustawienia" },
};

const STATUS_MAP = {
  ACTIVE: { label: "Aktywny", cls: "bg-emerald-100 text-emerald-700" },
  LEAD: { label: "Lead", cls: "bg-sky-100 text-sky-700" },
  INACTIVE: { label: "Nieaktywny", cls: "bg-slate-100 text-slate-500" },
  TODO: { label: "Do zrobienia", cls: "bg-slate-100 text-slate-700" },
  IN_PROGRESS: { label: "W toku", cls: "bg-blue-100 text-blue-700" },
  DONE: { label: "Wykonane", cls: "bg-emerald-100 text-emerald-700" },
};

const PRIORITY_MAP = {
  HIGH: { label: "Wysoki", cls: "text-red-600" },
  MEDIUM: { label: "Sredni", cls: "text-amber-600" },
  LOW: { label: "Niski", cls: "text-slate-500" },
};

function PreviewCard({ preview }) {
  if (!preview) return null;

  if (preview.type === "client") {
    const st = STATUS_MAP[preview.status] || STATUS_MAP.ACTIVE;
    return (
      <div className="space-y-3" data-testid="preview-card-client">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm text-slate-800 truncate pr-2">{preview.name}</h4>
          <Badge className={`rounded-full text-[10px] shrink-0 ${st.cls}`}>{st.label}</Badge>
        </div>

        <div className="grid grid-cols-1 gap-1.5 text-xs text-slate-600">
          {preview.nip && (
            <div className="flex items-center gap-1.5">
              <Briefcase className="h-3 w-3 text-slate-400 shrink-0" />
              <span>NIP: {preview.nip}</span>
            </div>
          )}
          {preview.email && (
            <div className="flex items-center gap-1.5">
              <Mail className="h-3 w-3 text-slate-400 shrink-0" />
              <span className="truncate">{preview.email}</span>
            </div>
          )}
          {preview.phone && (
            <div className="flex items-center gap-1.5">
              <Phone className="h-3 w-3 text-slate-400 shrink-0" />
              <span>{preview.phone}</span>
            </div>
          )}
          {preview.address && (
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3 w-3 text-slate-400 shrink-0" />
              <span className="truncate">{preview.address}</span>
            </div>
          )}
          {preview.owner && (
            <div className="flex items-center gap-1.5">
              <User className="h-3 w-3 text-slate-400 shrink-0" />
              <span>Opiekun: {preview.owner}</span>
            </div>
          )}
        </div>

        {preview.tags?.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            <Tag className="h-3 w-3 text-slate-400" />
            {preview.tags.slice(0, 4).map((t) => (
              <span key={t} className="px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-600">{t}</span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-1 border-t border-slate-100">
          <span className="text-[11px] text-slate-500">
            <CheckSquare className="h-3 w-3 inline mr-1" />
            {preview.openTasks} otwartych zadan
          </span>
          {preview.recentTasks?.length > 0 && (
            <span className="text-[10px] text-slate-400">
              Ostatnie: {preview.recentTasks[0]?.title?.substring(0, 25)}...
            </span>
          )}
        </div>
      </div>
    );
  }

  if (preview.type === "task") {
    const st = STATUS_MAP[preview.status] || STATUS_MAP.TODO;
    const pr = PRIORITY_MAP[preview.priority] || PRIORITY_MAP.MEDIUM;
    return (
      <div className="space-y-2.5" data-testid="preview-card-task">
        <div>
          <h4 className="font-semibold text-sm text-slate-800">{preview.title}</h4>
          {preview.clientName && (
            <p className="text-[11px] text-slate-500 mt-0.5">
              <Users className="h-3 w-3 inline mr-1" />
              {preview.clientName}
            </p>
          )}
        </div>

        {preview.description && (
          <p className="text-xs text-slate-600 leading-relaxed">{preview.description}</p>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`rounded-full text-[10px] ${st.cls}`}>{st.label}</Badge>
          <span className={`text-[11px] font-medium ${pr.cls}`}>
            <AlertCircle className="h-3 w-3 inline mr-0.5" />
            {pr.label}
          </span>
          {preview.assignee && (
            <span className="text-[11px] text-slate-500">
              <User className="h-3 w-3 inline mr-0.5" />
              {preview.assignee}
            </span>
          )}
        </div>

        {preview.dueDate && (
          <div className="text-[11px] text-slate-500 pt-1 border-t border-slate-100">
            <Clock className="h-3 w-3 inline mr-1" />
            Termin: {new Date(preview.dueDate).toLocaleDateString("pl-PL")}
          </div>
        )}
      </div>
    );
  }

  return null;
}

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hoveredResult, setHoveredResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const inputRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery("");
      setResults([]);
      setSelectedIndex(0);
      setPreview(null);
      setHoveredResult(null);
    }
  }, [open]);

  const search = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/search/global", { params: { q: searchQuery, limit: 15 } });
      setResults(res.data.results || []);
      setSelectedIndex(0);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query, search]);

  const fetchPreview = useCallback(async (result) => {
    if (!result?.id || !["client", "task"].includes(result.type)) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await api.get(`/search/preview/${result.type}/${result.id}`);
      setPreview(res.data.preview);
    } catch {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const handleMouseEnter = useCallback((result) => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setHoveredResult(result);
      fetchPreview(result);
    }, 350);
  }, [fetchPreview]);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setHoveredResult(null);
      setPreview(null);
    }, 200);
  }, []);

  const goToResult = (result) => {
    setOpen(false);
    const typeConfig = RESULT_TYPES[result.type];
    if (!typeConfig) return;
    let path = typeConfig.path;
    if (result.id) {
      if (result.type === "client") path = `/klienci/${result.id}`;
      else if (result.type === "task") path = `/zadania/${result.id}`;
      else if (result.type === "event") path = `/kalendarz?event=${result.id}`;
      else if (result.type === "invoice") path = `/faktury/${result.id}`;
      else if (result.type === "document") path = `/dokumenty?id=${result.id}`;
    }
    navigate(path);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && results[selectedIndex]) {
      e.preventDefault();
      goToResult(results[selectedIndex]);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors"
        data-testid="global-search-trigger"
      >
        <Search className="h-4 w-4" />
        <span className="hidden md:inline">Szukaj...</span>
        <kbd className="hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-white rounded border border-slate-200">
          <Command className="h-2.5 w-2.5" />K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl p-0 gap-0 overflow-hidden" data-testid="global-search-dialog">
          <div className="flex items-center gap-3 px-4 py-3 border-b">
            <Search className="h-5 w-5 text-slate-400" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Szukaj klientow, zadan, faktur..."
              className="border-0 shadow-none focus-visible:ring-0 text-base p-0"
              data-testid="global-search-input"
            />
            {loading && <Clock className="h-4 w-4 text-slate-400 animate-spin" />}
          </div>

          <div className="flex">
            {/* Results list */}
            <div className={`max-h-[420px] overflow-y-auto ${hoveredResult && preview ? "w-[55%] border-r" : "w-full"}`}>
              {query.length < 2 ? (
                <div className="p-8 text-center text-sm text-slate-500">
                  <Search className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  Wpisz minimum 2 znaki aby wyszukac
                </div>
              ) : results.length === 0 && !loading ? (
                <div className="p-8 text-center text-sm text-slate-500">
                  Brak wynikow dla "{query}"
                </div>
              ) : (
                <div className="py-1">
                  {results.map((result, index) => {
                    const typeConfig = RESULT_TYPES[result.type] || RESULT_TYPES.client;
                    const Icon = typeConfig.icon;
                    const isPreviewable = ["client", "task"].includes(result.type);

                    return (
                      <button
                        key={`${result.type}-${result.id}-${index}`}
                        onClick={() => goToResult(result)}
                        onMouseEnter={() => isPreviewable && handleMouseEnter(result)}
                        onMouseLeave={handleMouseLeave}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                          index === selectedIndex ? "bg-slate-100" : "hover:bg-slate-50"
                        }`}
                        data-testid={`search-result-${index}`}
                      >
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${typeConfig.color}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-slate-800 truncate">{result.title}</div>
                          {result.subtitle && (
                            <div className="text-xs text-slate-500 truncate">{result.subtitle}</div>
                          )}
                        </div>
                        <Badge variant="outline" className={`rounded-full text-[10px] shrink-0 ${typeConfig.color}`}>
                          {typeConfig.label}
                        </Badge>
                        <ArrowRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Preview panel */}
            {hoveredResult && (preview || previewLoading) && (
              <div
                className="w-[45%] max-h-[420px] overflow-y-auto p-4 bg-slate-50/50"
                onMouseEnter={() => clearTimeout(hoverTimerRef.current)}
                onMouseLeave={handleMouseLeave}
                data-testid="search-preview-panel"
              >
                {previewLoading && !preview ? (
                  <div className="flex items-center justify-center h-full text-sm text-slate-400">
                    <Clock className="h-4 w-4 animate-spin mr-2" />
                    Ladowanie...
                  </div>
                ) : preview ? (
                  <PreviewCard preview={preview} />
                ) : null}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between px-4 py-2 border-t bg-slate-50 text-xs text-slate-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white rounded border">&#8593;&#8595;</kbd> nawiguj
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white rounded border">Enter</kbd> otworz
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white rounded border">Esc</kbd> zamknij
              </span>
            </div>
            <span className="text-slate-400">Najedz na wynik aby zobaczyc podglad</span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default GlobalSearch;
